/**
 * combined-listing-pdp.js
 *
 * Handles Shopify Combined Listings on the product page.
 *
 * On every option change (Base, Color, Weight, or any other option):
 *   - Determines the correct target product URL
 *     (a different child URL for Base changes, the same URL for Color/Weight)
 *   - Fetches that product's section via the Section Rendering API
 *   - Replaces the section in the DOM with the fresh server-rendered HTML
 *
 * This guarantees the gallery, price, and availability always reflect the
 * selected variant — no manual variant matching needed.
 */

(function () {
  'use strict';

  // -------------------------------------------------------------------------
  // Helpers
  // -------------------------------------------------------------------------

  /**
   * Build the &option_values= query param from currently checked inputs.
   * Includes the input that just changed (already checked by the browser
   * before the change event fires).
   */
  function getOptionValuesParam(sectionEl) {
    var ids = Array.from(
      sectionEl.querySelectorAll('input[type="radio"]:checked[data-option-value-id]')
    ).map(function (el) { return el.dataset.optionValueId; });
    return ids.length ? '&option_values=' + ids.join(',') : '';
  }

  /**
   * Re-execute all <script> tags in a freshly inserted section so that
   * inline product data and custom elements reinitialise correctly.
   * DOMParser produces inert script nodes that don't auto-run.
   */
  function reExecuteScripts(sectionEl) {
    sectionEl.querySelectorAll('script').forEach(function (oldScript) {
      var newScript = document.createElement('script');
      Array.from(oldScript.attributes).forEach(function (attr) {
        newScript.setAttribute(attr.name, attr.value);
      });
      newScript.textContent = oldScript.textContent;
      oldScript.parentNode.replaceChild(newScript, oldScript);
    });
  }

  // -------------------------------------------------------------------------
  // Core: fetch and swap section
  // -------------------------------------------------------------------------

  async function swapSection(targetProductUrl, sectionEl) {
    var sectionId = sectionEl.dataset.sectionId;
    var optionValuesParam = getOptionValuesParam(sectionEl);
    var fetchUrl = targetProductUrl + '?section_id=' + sectionId + optionValuesParam;

    sectionEl.style.opacity = '0.5';
    sectionEl.style.pointerEvents = 'none';

    try {
      var response = await fetch(fetchUrl);

      if (!response.ok) {
        console.error('[Combined Listing] Fetch failed:', response.status, fetchUrl);
        sectionEl.style.opacity = '';
        sectionEl.style.pointerEvents = '';
        return;
      }

      var text = await response.text();
      var html = new DOMParser().parseFromString(text, 'text/html');
      var newSectionEl = html.getElementById('combined-listing-section-' + sectionId);

      if (!newSectionEl) {
        console.error(
          '[Combined Listing] Could not find #combined-listing-section-' + sectionId +
          ' in fetched response. Check the id attribute on the else-branch ' +
          'wrapper div in section.liquid.'
        );
        sectionEl.style.opacity = '';
        sectionEl.style.pointerEvents = '';
        return;
      }

      sectionEl.parentNode.replaceChild(newSectionEl, sectionEl);

      // Update URL so sharing, back-button, and Add to Cart all work correctly
      if (window.history && history.replaceState) {
        window.history.replaceState({}, '', targetProductUrl);
      }

      reExecuteScripts(newSectionEl);

    } catch (err) {
      console.error('[Combined Listing] Error during section swap:', err);
      if (sectionEl.isConnected) {
        sectionEl.style.opacity = '';
        sectionEl.style.pointerEvents = '';
      }
    }
  }

  // -------------------------------------------------------------------------
  // Page-load init
  //
  // If the browser lands on a Combined Listing parent product page (where
  // checked swatch inputs point to a different child URL), swap immediately
  // to the child so VariantPicker boots with real child variant data.
  // -------------------------------------------------------------------------

  function initCombinedListingOnLoad() {
    var sectionEl = document.querySelector(
      '[id^="combined-listing-section-"][data-product-url]'
    );
    if (!sectionEl) return;

    var currentProductUrl = sectionEl.dataset.productUrl;

    var checkedInputs = Array.from(
      sectionEl.querySelectorAll('input[type="radio"]:checked[data-product-url]')
    );

    var childProductUrl = null;
    for (var i = 0; i < checkedInputs.length; i++) {
      var url = checkedInputs[i].dataset.productUrl;
      if (url && url !== currentProductUrl) {
        childProductUrl = url;
        break;
      }
    }

    if (!childProductUrl) return;

    swapSection(childProductUrl, sectionEl);
  }

  // -------------------------------------------------------------------------
  // Option change handler
  //
  // Fires on every radio change in the capture phase (before theme JS).
  // For any input inside a Combined Listing section, intercepts the event
  // and does a fresh section fetch regardless of which option changed.
  // -------------------------------------------------------------------------

  async function handleOptionChange(event) {
    var input = event.target;
    if (!input || input.type !== 'radio') return;

    var sectionEl = input.closest('[data-product-url][data-section-id]');
    if (!sectionEl) return;

    // Stop the theme's VariantPicker from also handling this — it will crash
    // on Combined Listing pages due to the parent/child option count mismatch
    event.stopImmediatePropagation();

    // Determine target URL:
    // - Base option (has data-product-url) → use the child product URL
    // - Color / Weight / any other option (no data-product-url) → stay on
    //   the current product URL but re-fetch with new option_values
    var targetUrl = (input.dataset.productUrl && input.dataset.productUrl !== sectionEl.dataset.productUrl)
      ? input.dataset.productUrl
      : sectionEl.dataset.productUrl;

    await swapSection(targetUrl, sectionEl);
  }

  // -------------------------------------------------------------------------
  // Register
  // -------------------------------------------------------------------------

  document.addEventListener('change', handleOptionChange, { capture: true });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCombinedListingOnLoad);
  } else {
    initCombinedListingOnLoad();
  }

})();